
import { AfterViewInit, Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import L from 'leaflet';
import { FuseCardModule } from '../../../../@fuse/components/card/card.module';
import { GeopositionMarkerData } from '../../../modules/admin/tasks/tasks.types';
import { Utils } from '../../utils/utils';

@Component({
    selector: 'geoposition-map',
    imports: [FuseCardModule, MatIcon, TranslocoModule],
    templateUrl: './geoposition-map.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './geoposition-map.component.scss'
})
export class GeopositionMapComponent<T extends GeopositionMarkerData> implements AfterViewInit {
    private _geopositionSummaryList: T[] = [];
  
    @Input() 
    set geopositionSummaryList(value: T[]) {
        this._geopositionSummaryList = value;
        if (this.map) {
            this.updateMarkers();
        }
    }
    
    get geopositionSummaryList(): T[] {
        return this._geopositionSummaryList;
    }
    
    // Map variables
    mapId = 'map' + Utils.generateRandomId();
    protected markerList: L.Marker[] = [];
    protected iconNames: string[] = ["position-marker-1", "position-marker-2", "position-marker-3", "position-marker-4", "position-marker-5"];
    protected markerPath: string = "assets/images/position-markers/";

    protected defaultIcon = L.icon({
        iconUrl: '/leaflet/images/marker-icon.png',
        shadowUrl: '/leaflet/images/marker-shadow.png',
        iconRetinaUrl: '/leaflet/images/marker-icon.png',
        iconSize: [25, 41], // size of the icon
        shadowSize: [32, 32], // size of the shadow
        iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
        shadowAnchor: [2, 30],  // the same for the shadow
    });
    
    protected map: L.Map = null;

    ngAfterViewInit(): void {
        this.removeInvalidGeopositionSummary();
        this.initMap();
    }

    protected initMap(): void {
        //Create new map centered on Madrid
        if (!this.map) {
            this.map = L.map(this.mapId, {
                center: [40.416877, -3.703526],
                zoom: this.getZoom(),
                doubleClickZoom: false,
            });
        }

        this.updateMarkers();

        //Set source of tiles
        const tiles = L.tileLayer(
            'https://maps.krama.es/hot/{z}/{x}/{y}.png',
            {
                maxZoom: 18,
                minZoom: 8,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            });

        // Add tile to map
        tiles.addTo(this.map);
    }

    protected updateMarkers() {
        this.removeMarkers();
        this.addMarkers();
        this.defaultCenter();
    }

    protected defaultCenter() {
        if (this.geopositionSummaryList?.length > 0) {
            this.centerMap(this.mediaLatitud(this.geopositionSummaryList), this.mediaLongitud(this.geopositionSummaryList));
        } else {
            this.centerMap(40.416877, -3.703526);
        }
    }

    protected addMarkers() {
        if (this.geopositionSummaryList?.length > 1) {
            this.geopositionSummaryList.forEach((geopositionSummary, index) => {
                this.addMarker(geopositionSummary, this.iconNames[index]);
            })
        } else if (this.geopositionSummaryList?.length === 1) {
            this.addMarker(this.geopositionSummaryList[0]);
        }
    }

    protected getZoom() {
        if (this.geopositionSummaryList) {
            for (const geoposition1 of this.geopositionSummaryList) {
                for (const geoposition2 of this.geopositionSummaryList ) {
                    if (geoposition1.latitude > geoposition2.latitude && geoposition1.latitude - geoposition2.latitude > 0.015) {
                        return  14;
                    } else if (geoposition2.latitude > geoposition1.latitude && geoposition2.latitude - geoposition1.latitude > 0.015) {
                        return 14;
                    }
                    if (geoposition1.longitude > geoposition2.longitude && geoposition1.longitude - geoposition2.longitude > 0.015) {
                        return  14;
                    } else if (geoposition2.longitude > geoposition1.longitude && geoposition2.longitude - geoposition1.longitude > 0.015) {
                        return 14;
                    }
                }
            }
            return 15;
        } else {
            return 15;
        }
    }

    protected mediaLatitud(geopositionSummary: T[]) {
        let media: number = 0;
        for (const geoposition of geopositionSummary ) {
            media = media + geoposition.latitude;
        }
        media = media / geopositionSummary.length;
        return media;
    }

    protected mediaLongitud(geopositionSummary: T[]) {
        let media: number = 0;
        for (const geoposition of geopositionSummary ) {
            media = media + geoposition.longitude;
        }
        media = media / geopositionSummary.length;
        return media;
    }

    protected removeMarkers() {
        for(const marker of this.markerList) {
            if(marker)
                this.map.removeLayer(marker);
        }
    }
    
    protected addMarker(geoposition: T, iconName?: string): void {
        if (!geoposition || !geoposition.latitude || !geoposition.longitude) {
            this.markerList.push(null);
            return;
        }

        let icon: L.Icon = this.defaultIcon;
        if (iconName) {
            const url = this.markerPath + iconName + ".png";
            icon = L.icon({
                ...this.defaultIcon.options,
                iconUrl: url,
                iconRetinaUrl: url,
                iconSize: [45, 45]
            });
        }
        let marker = L.marker(L.latLng(geoposition.latitude, geoposition.longitude), { icon });
        
        if(marker) {
            this.markerList.push(marker);
            this.map.addLayer(marker);
        }
        else {
            this.markerList.push(null);
        }
    }

    protected centerMap(lat: number, lng: number) {
        this.map.setView({ lat, lng }, this.map.getZoom(), { animate: true, duration: 1 });
    }

    goToMarker(num: number) {
        let geoposition: T = this.geopositionSummaryList[num];
        this.centerMap(geoposition.latitude, geoposition.longitude);
    }

    getMarkerIcon(i) : string {
        return this.iconNames[i];
    }

    protected removeInvalidGeopositionSummary() {
        if(!this.geopositionSummaryList) return;
        this.geopositionSummaryList = this.geopositionSummaryList.filter(g => g.latitude && g.longitude);
    }
}
